// modules/WorldNPC.js（重构版）
// 世界场景NPC管理主控制器 - 协调各个NPC模块
import DebugHelper from './DebugHelper.js';
import { GAME_CONSTANTS } from './GameConstants.js';
import WorldNPCManager from './WorldNPC/WorldNPCManager.js';
import WorldNPCDialog from './WorldNPC/WorldNPCDialog.js';
import WorldNPCEvent from './WorldNPC/WorldNPCEvent.js';
import WorldNPCAnimation from './WorldNPC/WorldNPCAnimation.js';

export default class WorldNPC {
    constructor(scene) {
        this.scene = scene;
        this.debugMode = false;
        
        // 初始化各个子模块
        this.manager = new WorldNPCManager(scene);
        this.dialog = new WorldNPCDialog(scene, this.manager);
        this.event = new WorldNPCEvent(scene, this.manager);
        this.animation = new WorldNPCAnimation(scene, this.manager);
    }
    
    /**
     * 初始化NPC系统
     * @param {Array} npcConfigs - NPC配置数组
     */
    initialize(npcConfigs) {
        // 初始化管理器
        this.manager.initialize(npcConfigs);
        
        // 创建所有NPC
        this.manager.createAllNPCs();
        
        // 为每个NPC设置动画和AI
        this.manager.npcs.forEach((npc, index) => {
            const config = npc.config;
            const texture = config.texture;
            
            // 设置NPC动画
            if (texture) {
                this.animation.setupNPCWalkAnimations(npc.sprite, texture);
            }
            
            // 设置走动AI（根据配置中的 move 字段决定是否启用）
            const canMove = config.move !== false; // 只有明确设置为 false 时才不移动
            if (canMove) {
                this.animation.setupNPCWalkAI(npc.sprite, index);
            } else {
                // 不能移动的NPC：设置随机延迟播放动画
                const randomDelay = Math.random() * 4000; // 0-4000毫秒
                DebugHelper.log(`WorldNPC: NPC ${config.name} 设置为不可移动，将在 ${randomDelay.toFixed(0)}ms 后播放动画`);
                
                // 使用场景定时器延迟播放动画
                this.scene.time.delayedCall(randomDelay, () => {
                    if (npc.sprite && npc.sprite.active) {
                        this.animation.playNPCIdleAnimation(npc.sprite);
                    }
                });
            }
        });
        
        // 设置调试模式
        this.setupDebugMode();
        
        // 检查并触发跨场景事件（延迟执行，确保场景完全加载）
        this.scene.time.delayedCall(100, () => {
            this.event.checkAndTriggerEvents();
        });
        
        DebugHelper.log(`WorldNPC: 初始化完成，创建了 ${this.manager.npcs.length} 个NPC`);
    }
    
    /**
     * 添加NPC到障碍组（如果需要）
     * @param {Phaser.Physics.Arcade.Group} obstacleGroup - 障碍物组
     */
    addToObstacleGroup(obstacleGroup) {
        this.manager.addToObstacleGroup(obstacleGroup);
    }
    
    /**
     * 获取最近的可交互NPC
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     * @param {string} playerDirection - 玩家面向方向 ('up', 'down', 'left', 'right')
     * @param {number} radius - 交互半径（默认一格，32像素）
     * @returns {Object|null} 最近的可交互NPC
     */
    getNearestInteractableNPC(playerX, playerY, playerDirection, radius = 32) {
        return this.manager.getNearestInteractableNPC(playerX, playerY, playerDirection, radius);
    }
    
    /**
     * 获取NPC对话
     * @param {string|Object} npc - NPC ID或NPC对象
     * @returns {string|null} NPC对话内容
     */
    getNPCDialog(npc) {
        return this.dialog.getNPCDialog(npc);
    }
    
    /**
     * 让NPC转向面向玩家
     * @param {Phaser.GameObjects.Sprite} npcSprite - NPC精灵
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     */
    turnNPCTowardsPlayer(npcSprite, playerX, playerY) {
        this.animation.turnNPCTowardsPlayer(npcSprite, playerX, playerY);
    }
    
    /**
     * 获取所有NPC
     * @returns {Array} 所有NPC列表
     */
    getAllNPCs() {
        return this.manager.getAllNPCs();
    }
    
    /**
     * 设置调试模式
     */
    setupDebugMode() {
        // 调试快捷键（按F3显示/隐藏NPC调试信息）
        this.scene.input.keyboard.on('keydown-F3', () => {
            this.debugMode = !this.debugMode;
            this.toggleDebugDisplay();
            DebugHelper.log(`WorldNPC: 调试模式 ${this.debugMode ? '开启' : '关闭'}`);
        });
        
        // 按F4显示所有NPC位置
        this.scene.input.keyboard.on('keydown-F4', () => {
            this.logAllNPCPositions();
        });
    }
    
    /**
     * 切换调试显示
     */
    toggleDebugDisplay() {
        this.manager.npcs.forEach(npc => {
            if (npc.sprite && npc.sprite.getData('debugText')) {
                npc.sprite.getData('debugText').setVisible(this.debugMode);
            }
        });
    }
    
    /**
     * 记录所有NPC位置
     */
    logAllNPCPositions() {
        DebugHelper.group('NPC位置信息', () => {
            this.manager.npcs.forEach((npc, index) => {
                const name = npc.data.name || '未知NPC';
                const pos = npc.sprite ? `(${npc.sprite.x.toFixed(1)}, ${npc.sprite.y.toFixed(1)})` : '(已销毁)';
                DebugHelper.log(`${index + 1}. ${name}: ${pos} - 障碍: ${npc.data.obstacle ? '是' : '否'}`);
            });
        });
    }
    
    /**
     * 更新NPC状态（每帧调用）
     */
    update() {
        this.animation.update();
        this.updateInteractableHighlight();
    }

    /**
     * 高亮当前可交互的 NPC
     */
    updateInteractableHighlight() {
        this.manager.clearInteractableHighlight();

        const playerSprite = this.scene.playerSprite;
        if (!playerSprite || this.scene.worldUI?.isDialogActive) {
            return;
        }

        let direction = 'down';
        if (this.scene.worldPlayer) {
            direction = this.scene.worldPlayer.getCurrentDirection();
        }

        const target = this.manager.getNearestInteractableNPC(
            playerSprite.x,
            playerSprite.y,
            direction,
            GAME_CONSTANTS.INTERACTION.NPC_INTERACTION_RADIUS
        );

        if (target?.sprite) {
            this.manager.setInteractableHighlight(target.sprite);
        }
    }
    
    /**
     * 销毁所有NPC
     */
    destroy() {
        this.manager.destroy();
    }
}
